import { useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Modal, Spin } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';

import { useGetQuizzesDataQuery } from '@/api/endpoints/lmsData';
import { useGetSchoolDataQuery } from '@/api/endpoints/schools';
import { useAttachTestMutation } from '@/api/endpoints/schools/schoolSelectionPanel/tests/service';
import { DataSection, SelectField } from '@/shared/components';
import { useApiRequest } from '@/shared/hooks';

import styles from './AttachTestModal.module.scss';
import { TEST_ATTACHMENT_VALIDATION_SCHEMA } from './consts';
import type { IFormData } from './types';

interface IProp {
  isModalOpen: boolean;
  handleCancel: VoidFunction;
  schoolId: number;
}

export const AttachTestModal = ({ isModalOpen, handleCancel, schoolId }: IProp) => {
  const [attachTest, { isLoading: isTestAttaching }] = useAttachTestMutation();
  const { data: schoolData } = useGetSchoolDataQuery(
    { id: schoolId },
    {
      skip: !isModalOpen,
    },
  );
  const { data: quizzesData, isLoading: areQuizzesLoading } = useGetQuizzesDataQuery(undefined, {
    skip: !isModalOpen,
  });

  const sendApiRequest = useApiRequest();

  const quizzesList = useMemo(
    () =>
      quizzesData?.reduce(
        (prevVal, { account_id: accountId, id, name }) => {
          if (accountId === schoolData?.lms_account_id) {
            return [...prevVal, { value: id, label: name }];
          }

          return prevVal;
        },
        [] as { value: number; label: string }[],
      ),
    [quizzesData, schoolData?.lms_account_id],
  );

  const methods = useForm<IFormData>({
    defaultValues: {
      testId: undefined,
    },
    resolver: yupResolver(TEST_ATTACHMENT_VALIDATION_SCHEMA),
  });

  const { handleSubmit } = methods;

  const attachTestHandle = handleSubmit(({ testId }) => {
    sendApiRequest({
      successMessage: 'Тест успешно привязан',
      apiRequestCallback: () =>
        attachTest({
          schoolId,
          lmsCourseId: testId,
        }),
      successCallback: handleCancel,
    });
  });

  return (
    <Modal
      width={600}
      open={isModalOpen}
      onCancel={handleCancel}
      footer={null}
      centered
    >
      <Spin spinning={areQuizzesLoading || isTestAttaching}>
        <FormProvider {...methods}>
          <div className={styles.wrapper}>
            <p className={styles.title}>Привязать тест из LMS</p>
            <p className={styles.description}>
              Выберите тест, который вы создали в системе управления обучением (LMS), для привязки к данной школе
            </p>
            <DataSection
              titleText="Выберите тест"
              isTitleRequired
            >
              <SelectField
                fieldName="testId"
                options={quizzesList}
                placeholder="Выберите тест из списка..."
              />
            </DataSection>
            <p className={styles.annotation}>
              <b className={styles['bold-text']}>Примечание:</b> После привязки теста участники смогут приступить к его
              прохождению
            </p>
            <div className={styles['buttons-wrapper']}>
              <Button onClick={handleCancel}>Отмена</Button>
              <Button
                type="primary"
                onClick={attachTestHandle}
                disabled={isTestAttaching}
              >
                Привязать тест
              </Button>
            </div>
          </div>
        </FormProvider>
      </Spin>
    </Modal>
  );
};
